<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'
import type { ChipRow } from '@/browse/browse_state'

const props = withDefaults(defineProps<{
  rows: ChipRow[]
  chipLabel?: (key: string, value: string) => string
  removeButtonName?: (key: string, label: string) => string
}>(), {
  chipLabel: (_key: string, value: string) => value,
  removeButtonName: (_key: string, label: string) => `Remove ${label} filter`
})

const emit = defineEmits<{ remove: [key: string, value: string] }>()

const labelledRowsWithChips = computed(() => props.rows
  .filter((row) => row.chips.length > 0)
  .map((row) => ({
    ...row,
    chips: row.chips.map((chip) => ({ ...chip, label: props.chipLabel(chip.key, chip.value) }))
  })))

const chipsContainer = useTemplateRef<HTMLElement>('chipsContainer')
const removedChipPosition = ref<number | null>(null)

function removeButtons(): HTMLButtonElement[] {
  return [...(chipsContainer.value?.querySelectorAll('button') ?? [])]
}

function removeChip(event: MouseEvent, key: string, value: string) {
  removedChipPosition.value = removeButtons().indexOf(event.currentTarget as HTMLButtonElement)
  emit('remove', key, value)
}

// The removed chip's button leaves the page, so focus moves to the chip that takes its place.
function focusChipAtRemovedPosition() {
  const position = removedChipPosition.value
  if (position === null) {
    return
  }
  removedChipPosition.value = null
  const remainingButtons = removeButtons()
  remainingButtons[Math.min(position, remainingButtons.length - 1)]?.focus()
}

watch(labelledRowsWithChips, focusChipAtRemovedPosition, { flush: 'post' })
</script>

<template>
  <div v-if="labelledRowsWithChips.length" ref="chipsContainer" class="mb-3 border-b-2 border-gray-300">
    <div v-for="row in labelledRowsWithChips" :key="row.label" class="mb-[15px] flex flex-wrap items-center gap-2 text-text-md text-gray-900">
      <span v-if="row.label">{{ row.label }}:</span>
      <span
        v-for="chip in row.chips"
        :key="`${chip.key}:${chip.value}`"
        class="inline-flex items-center gap-2 rounded-sm border border-blue-400 bg-blue-200 py-1 pl-2 pr-2.5 leading-5"
      >
        {{ chip.label }}
        <button
          type="button"
          class="material-icons-round text-[1em] text-blue-400"
          :aria-label="removeButtonName(chip.key, chip.label)"
          @click="removeChip($event, chip.key, chip.value)"
        >
          close
        </button>
      </span>
    </div>
  </div>
</template>
